import { useState, useCallback, useEffect } from 'react';
import type { FormConfiguration, FormQuestion } from '@/types';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface UseFormConfigurationsReturn {
  configs: FormConfiguration[];
  loading: boolean;
  getForm: (formType: string) => FormConfiguration | undefined;
  updateQuestions: (formType: string, questions: FormQuestion[]) => Promise<void>;
  resetToDefaults: () => void;
}

export function useFormConfigurations(): UseFormConfigurationsReturn {
  const { user } = useAuth();
  const [configs, setConfigs] = useState<FormConfiguration[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('form_configurations')
        .select('*')
        .order('form_type');

      if (error) throw error;

      const mapped: FormConfiguration[] = (data || []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        formType: row.form_type as 'epi' | 'incident' | 'denunciation',
        questions: (row.questions as FormQuestion[]) || [],
        version: row.version as number,
        updatedAt: row.updated_at as string,
      }));

      setConfigs(mapped);
    } catch {
      // fallback: keep current state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const getForm = useCallback(
    (formType: string) => configs.find((f) => f.formType === formType),
    [configs]
  );

  const updateQuestions = useCallback(async (formType: string, questions: FormQuestion[]) => {
    if (!user || user.perfil !== 'admin') return;

    const current = configs.find((f) => f.formType === formType);
    const newVersion = (current?.version || 0) + 1;

    const { error } = await (supabase.from('form_configurations') as any)
      .update({
        questions: questions as unknown as Record<string, unknown>,
        version: newVersion,
        updated_at: new Date().toISOString(),
      })
      .eq('form_type', formType);

    if (error) throw error;

    await fetchConfigs();
  }, [user, configs, fetchConfigs]);

  const resetToDefaults = useCallback(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  return { configs, loading, getForm, updateQuestions, resetToDefaults };
}
